/**
 * What the crew does at a stop, in words.
 *
 * The owner, Sep 30 2026: "Change all mowing just on to do on crew side to say
 * mowing and if it's anything additional add as additional not instead."
 *
 * A mowing visit is "Mowing" on every crew screen — never "First mow", never
 * "Biweekly mowing — Nicole Johnson". Anything else done at that yard on that
 * visit sits beside it as Additional (add-ons, the bed-spray flag, a lawn-plan
 * round riding the mow, tagged services, extra work the title names). Fees and
 * credits (fee_*) are money, not work, and are never listed. A job that is not
 * a mow keeps its own name.
 *
 * The server stamps work_main / work_additional on crew payloads
 * (server/utils/crewWork.js, which also knows the add-ons and riders); this
 * file reads those first and builds the same words from the row when they are
 * missing (an offline copy, an office list). Same rules on both sides — keep
 * them in step. Pure: no fetching here.
 */

export type WorkAddon = { addon_key?: string | null; name?: string | null };
export type WorkJob = {
  service_type?: string | null;
  title?: string | null;
  customer_name?: string | null;
  service_tags?: string | null;
  bed_spray?: number | boolean | null;
  spray_needed?: number | boolean | null;
  route_id?: number | null;
  recurring_schedule_id?: number | null;
  address?: string | null;
  work_main?: string | null;
  work_additional?: string[] | null;
};
export type Work = { main: string; additional: string[] };

const MOW_WORD = /mow/i;

// The crew's names for a job's type (server: portalVisits.SERVICE_WORDS + crewWork.CREW_WORDS).
const CREW_WORDS: Record<string, string> = {
  mowing: 'Mowing', estimate: 'Quote visit', treatment: 'Lawn treatment', lawn_treatment: 'Lawn treatment',
  weed_spray: 'Weed spray', fix: 'Follow-up visit', trimming: 'Bush trimming', beds_nutsedge_spray: 'Beds & nutsedge spray',
  irrigation: 'Irrigation', mulch: 'Mulch', cleanup: 'Cleanup', project: 'Project', todo: 'To-do',
  bed_spray: 'Bed spray', bush_trimming: 'Bush trimming', tree_trimming: 'Tree trimming', edging: 'Edging',
};

const truthy = (v: unknown) => v === true || Number(v) === 1;

function sentence(s: string | null | undefined): string {
  const t = String(s || '').trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
}

/** A type as the crew reads it: 'beds_nutsedge_spray' → 'Beds & nutsedge spray'. */
export function serviceWords(type: string | null | undefined): string {
  const t = String(type || '').trim().toLowerCase();
  if (!t) return '';
  if (MOW_WORD.test(t)) return 'Mowing';
  if (CREW_WORDS[t]) return CREW_WORDS[t];
  return sentence(t.replace(/[_\s]+/g, ' '));
}

/**
 * A mowing visit: its type says mow. With no type at all, a route stop, a
 * series visit, an untitled job or one whose title says mow is a mow.
 */
export function isMowJob(j: WorkJob | null | undefined): boolean {
  const type = String(j?.service_type ?? '').trim().toLowerCase();
  if (type) return MOW_WORD.test(type);
  const title = String(j?.title || '').trim();
  return !!j?.route_id || !!j?.recurring_schedule_id || !title || MOW_WORD.test(title);
}

function namesCustomer(part: string, customerName: string | null | undefined): boolean {
  const name = String(customerName || '').trim().toLowerCase();
  if (!name) return false;
  const p = String(part || '').trim().toLowerCase();
  if (p.includes(name)) return true;
  return name.split(/\s+/).filter(w => w.length >= 3).some(w => p === w || p.startsWith(`${w}'s`) || p.startsWith(`${w}’s`));
}

const MOW_PART = /^(?:(?:the|a|our|first|one[-\s]?time|weekly|bi-?weekly|every other week|every two weeks|monthly|regular|recurring|lawn|yard|grass|front|full)\s+)*(?:mow|mows|mowed|mowing|cut|cutting|visit|service|job|lawn care)(?:\s+(?:visit|service|only|job))?$/i;
// Edge, trim and blow are part of every mow — not extra work. The bid's own
// mow line says them ("mow, edge, trim, and blow-off"), and so do typed ones
// ("string trimming", "blow off hard surfaces", "blow-off the driveway").
const ROUTINE = /^(?:edge|edges|edged|edging|trim|trims|trimmed|trimming|string[\s-]?trim(?:s|med|ming)?|weed[\s-]?eat(?:ing)?|clean(?:[\s-]?up)?\s+(?:the\s+)?clippings|blow|blows|blowing|blow(?:s|ing)?[\s-]+off(?:\s+.*)?|blow(?:s|ing)?\s+(?:the\s+)?(?:hard\s+surfaces?|driveways?|drives?|walks?|walkways?|sidewalks?|patios?|curbs?|clippings))$/i;
const GENERIC = /^(job|service|visit|work|lawn plan)$/i;
// Money is never work, and the crew never see a price: "$45 a visit",
// "(10% weekly discount applied)".
const MONEY = /\$|%|\bdiscount\b/i;

// A street address is where the work is, never work itself. A job can carry its yard's address as its title or a
// line ("616 Garner Park Drive"), and the crew read it as "Additional: 616 Garner Park Drive" (owner, Oct 14 2026:
// "why does it say that? ... aditional work mow"). A piece that starts with a house number and ends in a street
// word, or that is the job's own street, is left out.
const STREET = /^\d{1,6}[a-z]?(?:\s*\/\s*\d{1,6}[a-z]?)?\s+(?:[nsew]\.?\s+)?(?:[a-z0-9.'’-]+\s+)*?(?:st|street|dr|drive|rd|road|ln|lane|ct|court|cv|cove|blvd|boulevard|ave|avenue|way|trl|trail|pkwy|parkway|cir|circle|pl|place|loop|hwy|highway|xing|crossing|ter|terrace|bnd|bend|sq|square|holw|hollow|gln|glen|rdg|ridge|crk|creek|hl|hill|hls|hills|mdw|meadow|mdws|meadows)\.?(?:\s+\d+[a-z]?)?(?:\s*(?:#|apt\.?|unit|ste\.?|suite)\s*[\w-]+)?(?:\s*,.*)?$/i;
const squash = (s: string | null | undefined) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const MONEY_BRACKETS = /\([^)]*(?:\$|%|\bdiscount\b)[^)]*\)/gi;
const ONE_TIME = /\(\s*one[-\s]?time\s*\)/i;
// The one-time work autoQuote.foldOneTimeIntoVisit puts on a first mow:
// "First visit — mow + <description> · <description>". Each description is
// one job, kept whole — its commas are its own words.
const FOLD = /^\s*first visit\s+[—–-]\s+mow\s+\+\s+/i;
// Clauses inside one piece, and the words joining one bit of work to the next.
const CLAUSES = /\s*[,;&/:]\s*/;
const JOINS = /\s+(?:and|with|plus|then|also)\s+/i;
const ATOMS = /\s*[,;&/:]\s*|\s+(?:and|with|plus|then|also)\s+/i;

function tidy(s: string | null | undefined): string {
  return String(s || '').trim().replace(/^(?:and|then|plus|with|also)\s+/i, '').replace(/[.!]+$/, '').trim();
}

/** Every bit of work a piece names is the mow itself (edge, trim, blow-off). */
function onlyRoutine(piece: string): boolean {
  const atoms = String(piece || '').split(ATOMS).map(tidy).filter(Boolean);
  return atoms.length > 0 && atoms.every(a => ROUTINE.test(a) || MOW_PART.test(a) || MOW_WORD.test(a));
}

/**
 * A piece that describes the mow: it starts with it ("Weekly mowing: mow,
 * edge…", "Mowing and spraying") or ends with it ("Weed spray and mow"). A
 * piece that only mentions mowing further in ("One-time cleanup, including
 * mowing down the back lot") is its own work.
 */
function describesMow(piece: string): boolean {
  const p = String(piece || '').trim();
  return MOW_WORD.test(p.split(CLAUSES)[0] || '') || /mow\w*[.!]*$/i.test(p);
}

/**
 * The work a mow's title (or a mow line's words) names beyond the mow.
 * "First visit — mow + Bush trimming · Mulch" → ['Bush trimming', 'Mulch'].
 * "Weekly mowing: mow, edge, trim, and blow-off (10% weekly discount
 * applied)" → []. Money never reaches the crew: a price or a discount is dropped.
 */
/** True when a piece is a street address, or the job's own street (`address`, as on the property). */
export function namesAddress(part: string | null | undefined, address?: string | null): boolean {
  const p = squash(part);
  if (!p) return false;
  if (STREET.test(String(part || '').trim())) return true;
  const a = squash(String(address || '').split(',')[0]);
  return !!a && (p === a || (p.length >= 6 && a.startsWith(p)));
}

export function titleExtras(title: string | null | undefined, customerName?: string | null, address?: string | null): string[] {
  const t = String(title || '')
    .replace(/\(\s*one[-\s]?time\s*\)/gi, ' ')
    .replace(MONEY_BRACKETS, ' ')
    .replace(/^\s*lawn plan:\s*/i, '');
  const out: string[] = [];
  const keep = (part: string) => {
    const p = tidy(part);
    if (!p || p.length < 2 || MONEY.test(p) || GENERIC.test(p) || namesCustomer(p, customerName) || namesAddress(p, address)) return;
    out.push(sentence(p));
  };
  if (FOLD.test(t)) {
    t.replace(FOLD, '').split(/\s+·\s+/).forEach(keep);
    return out;
  }
  for (const raw of t.split(/\s+[—–-]\s+|\s*[+·|;]\s*/)) {
    const piece = tidy(raw);
    if (!piece || MOW_PART.test(piece) || onlyRoutine(piece)) continue;
    if (describesMow(piece)) {
      // The mow's own words: whatever they add beyond the mow, one by one.
      for (const clause of piece.replace(/\([^)]*\)/g, ' ').split(CLAUSES).map(tidy)) {
        if (!clause || ROUTINE.test(clause)) continue;
        for (const atom of clause.split(JOINS).map(tidy)) {
          if (!atom || MOW_WORD.test(atom) || MOW_PART.test(atom) || ROUTINE.test(atom)) continue;
          keep(atom);
        }
      }
      continue;
    }
    keep(piece);
  }
  return out;
}

function cleanTitle(title: string | null | undefined, customerName: string | null | undefined): string {
  const t = String(title || '')
    .replace(/^\s*lawn plan:\s*/i, '')
    .replace(/\s*\(\s*one[-\s]?time\s*\)/gi, '')
    .trim();
  return t.split(/\s+[—–]\s+|\s+-\s+/)
    .map(s => s.trim())
    .filter(s => s && !GENERIC.test(s) && !s.includes('$') && !namesCustomer(s, customerName))
    .join(' — ');
}

function ownName(j: WorkJob): string {
  const type = String(j.service_type || '').trim().toLowerCase();
  if (type === 'estimate') return 'Quote visit';
  const title = cleanTitle(j.title, j.customer_name);
  if (title) return sentence(title);
  if (type === 'todo') return 'To-do';
  return serviceWords(type) || 'Job';
}

const FILLER = new Set(['and', 'the', 'a', 'plus', 'with']);

/**
 * The words that make two items the same job: case, punctuation and filler
 * dropped. The bed-spray add-on ("Bed weed spray") and the bed-spray flag or
 * tag ("Bed spray") are one job. Lawn "Weed spray" is not — that is the lawn.
 */
export function workKey(s: string | null | undefined): string {
  const k = String(s || '').toLowerCase().split(/[^a-z0-9]+/).filter(t => t && !FILLER.has(t)).join(' ');
  if (/^(?:beds? (?:weed )?spray(?:ing|ed)?|spray(?:ing)? (?:the )?beds?)$/.test(k)) return 'bed spray';
  return k;
}

/**
 * One list, no repeats. Two items are the same job only when their words
 * match (workKey), or when one is a single word the other names ("Fungus"
 * and "Fungus treatment") — then the fuller one is kept, never dropped.
 * Anything that says the main work again is left out.
 */
function dedupe(items: string[], main: string): string[] {
  const out: { w: string; k: string; tk: string[] }[] = [];
  const mk = workKey(main);
  const mtk = mk.split(' ');
  for (const item of items) {
    const w = String(item || '').trim();
    const k = workKey(w);
    if (!k) continue;
    const tk = k.split(' ');
    if (k === mk || (tk.length === 1 && mtk.includes(tk[0]))) continue;
    const i = out.findIndex(o => o.k === k || (tk.length === 1 && o.tk.includes(tk[0])) || (o.tk.length === 1 && tk.includes(o.tk[0])));
    if (i >= 0) {
      if (tk.length > out[i].tk.length) out[i] = { w, k, tk };
      continue;
    }
    out.push({ w, k, tk });
  }
  return out.map(o => o.w);
}

/**
 * A job line that is money, not work: the one-time visit fee, travel, the
 * overgrown catch-up charge, a recurring credit. A converted bid copies them
 * onto the job; the crew's work list leaves them out (server: crewWork.isFeeLine). So is a free
 * visit's own line, "Free mow — on us" (owner, Oct 14 2026: the crew read "On us" as work).
 */
const FEE_LINE = /\bfees?\b|\bcredit(?:s|ed)?\b|^\s*travel\b|\bcatch-?up\b.*\bfirst visit only\b|^\s*(?:deposit|discount)\b|\bon us\b|^\s*no charge\b/i;
export function isFeeLine(description: string | null | undefined): boolean {
  return FEE_LINE.test(String(description || ''));
}

/** Add-on rows → the work they name. Fees and credits (fee_*) are not work. */
function addonWords(addons: WorkAddon[] | null | undefined): string[] {
  return (addons || [])
    .filter(a => a && !String(a.addon_key || '').startsWith('fee_'))
    .map(a => String(a.name || '').trim())
    .filter(Boolean);
}

/**
 * → { main, additional }. `main` is "Mowing" for any mowing visit and the
 * job's own name otherwise; `additional` is everything else on the visit,
 * de-duplicated, in a stable order: add-ons, the bed-spray flag, lawn-plan
 * riders, tagged services, then the title's extras. Uses the server's stamped
 * words when present; `addons` (e.g. from /mow-addons/job/:id) are added in.
 */
export function workOf(j: WorkJob | null | undefined, addons?: WorkAddon[] | null): Work {
  const job: WorkJob = j || {};
  const extra = addonWords(addons);
  if (job.work_main) {
    const main = job.work_main;
    return { main, additional: dedupe([...(job.work_additional || []), ...extra], main) };
  }
  const mow = isMowJob(job);
  const type = String(job.service_type || '').trim().toLowerCase();
  const main = mow ? 'Mowing' : ownName(job);
  const add = [...extra];
  const sprayAddon = (addons || []).some(a => a && a.addon_key === 'bed_spray');
  if ((truthy(job.bed_spray) || truthy(job.spray_needed)) && !sprayAddon) add.push('Bed spray');
  for (const tag of String(job.service_tags || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean)) {
    if (mow ? MOW_WORD.test(tag) : tag === type) continue;
    add.push(serviceWords(tag));
  }
  if (mow) add.push(...titleExtras(job.title, job.customer_name, job.address));
  return { main, additional: dedupe(add, main) };
}

/** One line: "Mowing" or "Mowing · Additional: Weed treatment, Bed spray". */
export function workLine(j: WorkJob | null | undefined, addons?: WorkAddon[] | null): string {
  const w = workOf(j, addons);
  return w.additional.length ? `${w.main} · Additional: ${w.additional.join(', ')}` : w.main;
}

/**
 * A job's line items as the crew's work list. The recurring mow's own line
 * becomes one "Mowing" (plus anything it names beyond the mow, e.g. "Mowing
 * and spraying" → Mowing, Spraying); every other line — a one-time job, even
 * one that mentions mowing — stays exactly as written. Fee, travel and credit
 * lines are money, not work, and are left out.
 */
export function workItems(
  lines: { id?: number | string; description?: string | null; quantity?: number | string | null }[],
  customerName?: string | null,
  address?: string | null,
): { key: string; text: string; quantity: number }[] {
  const out: { key: string; text: string; quantity: number }[] = [];
  const have = new Set<string>();
  const push = (key: string, text: string, quantity = 1) => {
    const k = text.toLowerCase();
    if (!text || have.has(k)) return;
    have.add(k);
    out.push({ key, text, quantity });
  };
  (lines || []).forEach((li, i) => {
    const d = String(li.description || '').trim();
    const key = String(li.id ?? i);
    if (!d || isFeeLine(d)) return;
    // A line that is only the yard's address is the mow's own line, written as where it is.
    if (namesAddress(d, address)) { push(`${key}-mow`, 'Mowing'); return; }
    if (!ONE_TIME.test(d) && describesMow(d)) {
      push(`${key}-mow`, 'Mowing');
      titleExtras(d, customerName, address).forEach((x, n) => push(`${key}-${n}`, x));
    } else {
      push(key, d, Number(li.quantity) || 1);
    }
  });
  return out;
}
